import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaDollarSign, 
  FaClock,
  FaGraduationCap,
  FaIndustry,
  FaUserTie,
  FaCalendar,
  FaCheckCircle,
  FaVenusMars,
  FaStar,
  FaBolt,
  FaFire,
  FaHome,
  FaMedkit
} from 'react-icons/fa';
import axios from 'axios';
import { endpoints } from '../config/api';
import './JobDetail.css';
import ShareJobButtons from '../components/common/ShareJobButtons';
import LoadingSpinner from '../components/common/LoadingSpinner';

import CustomImage from '../components/common/CustomImage';
import { extractDataFromResponse, processJobsData } from '../utils/apiUtils';
import JobPostingSchema from '../components/seo/JobPostingSchema';
import { updatePageSEO, debugSEO } from '../utils/seoUtils';
import { getBestOpenGraphImage } from '../utils/imageUtils';
import BreadcrumbSchema from '../components/seo/BreadcrumbSchema';
import JobStatusBadges from '../components/jobs/JobStatusBadges';
import JobOverview from '../components/jobs/JobOverview';
import CompanyInfo from '../components/jobs/CompanyInfo';
import RecentJobs from '../components/home/RecentJobs';
import { AdUnitJobs } from '../components/adsense';

const JobDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [alertForm, setAlertForm] = useState({
    email: '',
    jobCategories: []
  });
  const [alertStatus, setAlertStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  // Update job categories array to match SearchContainer
  const jobCategories = [
    { value: 'accounting_finance', label: 'Accounting & Finance' },
    { value: 'data_analyst', label: 'Data Analyst' },
    { value: 'banking', label: 'Banking' },
    { value: 'bpo', label: 'BPO' },
    { value: 'business_development', label: 'Business Development' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'health_care', label: 'Health Care' },
    { value: 'management', label: 'Management' },
    { value: 'digital_marketing', label: 'Digital Marketing' },
    { value: 'electrical_electronics', label: 'Electrical & Electronics' },
    { value: 'engineering_technology', label: 'Engineering & Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'human_resource', label: 'Human Resource' },
    { value: 'it_software', label: 'IT & Software' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'research_development', label: 'Research & Development' },
    { value: 'retail_ecommerce', label: 'Retail & Ecommerce' }
  ];

  useEffect(() => {
    const fetchJob = async () => {
      if (!slug) {
        setError('Job slug is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Preload critical resources
        const preloadImage = (src) => {
          if (src && src !== '/images/company-placeholder.svg') {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            document.head.appendChild(link);
          }
        };
        
        // Optimize API call with timeout and caching
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        
        // Use the new slug-based endpoint from backend
        const response = await axios.get(`${endpoints.jobs.list}/slug/${slug}`, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'max-age=300' // 5 minutes cache
          }
        });
        
        clearTimeout(timeoutId);
        
        if (response.data.success) {
          const currentJob = response.data.data;
          const seoDataFromAPI = response.data.seo;

          // Process the job data with enhanced structure
          const processedJob = {
            ...currentJob,
            postedDate: currentJob.datePosted || currentJob.postedDate || currentJob.createdAt || currentJob.date,
            logo: currentJob.hiringOrganization?.logo || currentJob.logo || '/images/company-placeholder.svg',
            slug: currentJob.slug,
            // Ensure company structure exists
            hiringOrganization: currentJob.hiringOrganization || {
              name: currentJob.company || 'Company',
              logo: currentJob.logo,
              website: currentJob.companyWebsite,
              sameAs: currentJob.companyLinkedIn
            },
            // Ensure location structure exists
            location: currentJob.location || {
              type: 'on_site',
              address: currentJob.locationAddress || currentJob.location?.address || 'Not specified',
              city: currentJob.locationCity || currentJob.location?.city || '',
              state: currentJob.locationState || currentJob.location?.state || '',
              country: currentJob.locationCountry || currentJob.location?.country || '',
              postalCode: currentJob.locationPostalCode || currentJob.location?.postalCode || '',
              isRemote: currentJob.location?.isRemote || false
            },
            // Ensure salary structure exists
            salary: currentJob.salary || {
              min: currentJob.salaryMin || 0,
              max: currentJob.salaryMax || 0,
              currency: currentJob.salaryCurrency || 'USD',
              period: currentJob.salaryPeriod || 'yearly',
              display: currentJob.salaryDisplay || 'Salary not specified'
            },
            // Ensure employment type
            employmentType: currentJob.employmentType || currentJob.jobType || 'Full-time',
            // Ensure experience level
            experienceLevel: currentJob.experienceLevel || 'Not specified',
            // Ensure skills array
            skills: Array.isArray(currentJob.skills) ? currentJob.skills : [],
            // Ensure images array
            images: Array.isArray(currentJob.images) ? currentJob.images : [],
            // Handle application deadline
            applicationDeadline: currentJob.applicationDeadline || currentJob.validThrough || null,
            // Handle requirements
            requirements: currentJob.requirements || currentJob.content || currentJob.description || '',
            // Handle content
            content: currentJob.content || currentJob.description || '',
            // Handle description
            description: currentJob.description || currentJob.content || ''
          };

          setJob(processedJob);
          setSeoData(seoDataFromAPI);
          
          // Update SEO meta tags directly with job data for image fallback
          if (seoDataFromAPI) {
            // Pass processedJob as contentData to extract images if SEO data lacks them
            updatePageSEO(seoDataFromAPI, processedJob);
            
            // Debug in development
            if (process.env.NODE_ENV === 'development') {
              debugSEO();
            }
          } else {
            // If no SEO data from API, create basic SEO from job data
            const baseUrl = 'https://jobsopportunity.in';
            const jobUrl = `${baseUrl}/job/${processedJob.slug}`;
            const jobImage = getBestOpenGraphImage(processedJob, 'job');
            
            const fallbackSEO = {
              title: `${processedJob.title} - Jobs Opportunity`,
              description: processedJob.description || processedJob.content || 'Find your dream job',
              canonical: jobUrl,
              og: {
                title: `${processedJob.title} - Jobs Opportunity`,
                description: processedJob.description || processedJob.content || 'Find your dream job',
                url: jobUrl,
                type: 'website',
                site_name: 'Jobs Opportunity',
                image: jobImage,
                image_width: '1200',
                image_height: '630'
              },
              twitter: {
                card: 'summary_large_image',
                title: `${processedJob.title} - Jobs Opportunity`,
                description: processedJob.description || processedJob.content || 'Find your dream job',
                image: jobImage
              }
            };
            updatePageSEO(fallbackSEO, processedJob);
          }
          
          // Preload company logo if available
          if (processedJob.logo && processedJob.logo !== '/images/company-placeholder.svg') {
            preloadImage(processedJob.logo);
          }
          
          // Get related jobs from the same company or category
          setTimeout(async () => {
            try {
              const relatedResponse = await axios.get(endpoints.jobs.list);
              const relatedJobsData = extractDataFromResponse(relatedResponse, 'data');
              const allJobs = processJobsData(relatedJobsData);
              
              const related = allJobs
                .filter(job => 
                  job.id !== processedJob.id && 
                  (job.company === processedJob.company || 
                   job.employmentType === processedJob.employmentType)
                )
                .slice(0, 3);
              
              setRelatedJobs(related);
            } catch (error) {
              console.error('Error fetching related jobs:', error);
            }
          }, 100);
          
        } else {
          setError('Job not found');
          return;
        }

      } catch (error) {
        console.error('Error fetching job:', error);
        if (error.name === 'AbortError') {
          setError('Request timeout. Please try again.');
        } else {
          setError('Failed to load job');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const handleAlertInputChange = (e) => {
    const { name, value } = e.target;
    setAlertForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (e) => {
    const selectedCategories = Array.from(e.target.selectedOptions, option => option.value);
    setAlertForm(prev => ({
      ...prev,
      jobCategories: selectedCategories
    }));
  };

  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    setAlertStatus({ loading: true, success: false, error: null });

    // Validate input
    if (!alertForm.email) {
      setAlertStatus({
        loading: false,
        success: false,
        error: 'Email is required'
      });
      return;
    }

    if (!alertForm.jobCategories || alertForm.jobCategories.length === 0) {
      setAlertStatus({
        loading: false,
        success: false,
        error: 'Please select at least one job category'
      });
      return;
    }

    try {
      console.log('Submitting job alert subscription:', {
        email: alertForm.email,
        jobCategories: alertForm.jobCategories
      });

      const response = await axios.post(endpoints.jobAlerts.subscribe, {
        email: alertForm.email,
        jobCategories: alertForm.jobCategories
      });

      if (response.data.success) {
        setAlertStatus({
          loading: false,
          success: true,
          error: null
        });
        setAlertForm({ email: '', jobCategories: [] });
      } else {
        throw new Error(response.data.message || 'Failed to subscribe to job alerts');
      }
    } catch (err) {
      console.error('Job alert subscription error:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });

      let errorMessage = 'Failed to subscribe to job alerts. Please try again later.';
      
      if (err.response) {
        switch (err.response.status) {
          case 404:
            errorMessage = 'Job alerts service is currently unavailable. Please try again later.';
            break;
          case 400:
            errorMessage = err.response.data.message || 'Invalid input. Please check your email and selected categories.';
            break;
          case 500:
            errorMessage = err.response.data.message || 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.response.data.message || errorMessage;
        }
      }
      
      setAlertStatus({
        loading: false,
        success: false,
        error: errorMessage
      });
    }
  };

  if (loading) {
    return (
      <div className="job-detail-container loading">
        <div className="job-detail-header loading">
          <div className="company-info">
            <div className="company-logo-container">
              <div className="content-skeleton" style={{ width: '120px', height: '120px', borderRadius: '50%' }}></div>
            </div>
            <div className="header-content">
              <div className="content-skeleton title"></div>
              <div className="content-skeleton text short"></div>
              <div className="content-skeleton text medium"></div>
            </div>
          </div>
        </div>
        
        <div className="job-detail-content">
          <div className="main-content">
            <div className="content-skeleton title"></div>
            <div className="content-skeleton text"></div>
            <div className="content-skeleton text"></div>
            <div className="content-skeleton text short"></div>
            <div className="content-skeleton title"></div>
            <div className="content-skeleton text"></div>
            <div className="content-skeleton text"></div>
            <div className="content-skeleton text medium"></div>
          </div>
          
          <aside className="job-overview-sidebar">
            <div className="overview-card">
              <div className="content-skeleton title"></div>
              <div className="content-skeleton text"></div>
              <div className="content-skeleton text short"></div>
              <div className="content-skeleton text"></div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Job Not Found</h3>
        <p>{error}</p>
        
        {similarJobs.length > 0 && (
          <div className="similar-jobs">
            <h4>Similar Jobs You Might Like:</h4>
            <div className="similar-jobs-list">
              {similarJobs.map((job) => (
                <div key={job.id} className="similar-job-card" onClick={() => navigate(`/job/${job.slug}`)}>
                  <h5>{job.title}</h5>
                  <p className="company">{job.company || job.hiringOrganization?.name}</p>
                  <p className="location">{job.location?.address || job.location}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="error-actions">
          <button onClick={() => navigate('/jobs')} className="btn-primary">
            Browse All Jobs
          </button>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="not-found-container">
        <h3>Job Not Found</h3>
        <p>The job you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate('/jobs')}>Back to Jobs</button>
      </div>
    );
  }

  const renderSimilarJobs = () => {
    return relatedJobs.map(job => (
      <Link 
        key={job._id} 
        to={`/jobs/${job.slug}`}
        className="similar-job-card"
      >
        <div className="similar-job-info">
          <h4>{job.title}</h4>
          <span className="company">{job.hiringOrganization?.name || job.company}</span>
          <div className="job-brief">
            <span><FaMapMarkerAlt /> {job.location?.address || job.location}</span>
            <span><FaDollarSign /> {job.salary?.currency} {job.salary?.min} - {job.salary?.max} ({job.salary?.period})</span>
          </div>
        </div>
      </Link>
    ));
  };

  return (
    <div className={`job-detail-container ${loading ? 'loading' : ''}`}>
      {/* SEO Components */}
      {job && (
        <>
          <JobPostingSchema job={job} />
          <BreadcrumbSchema
            items={[
              { name: 'Home', url: 'https://jobsopportunity.in' },
              { name: 'Jobs', url: 'https://jobsopportunity.in/jobs' },
              { name: job.category || 'All Jobs', url: `https://jobsopportunity.in/search?category=${job.category}` },
              { name: job.title, url: `https://jobsopportunity.in/jobs/${job.slug}` }
            ]}
          />
        </>
      )}

      <div className={`job-detail-header ${loading ? 'loading' : ''}`}>
        <div className="company-info">
          <div className="company-logo-container">
            <CustomImage
              src={job.hiringOrganization?.logo}
              alt={job.hiringOrganization?.name}
              className="company-logo"
              fallbackSrc="/images/company-placeholder.svg"
              priority={true}
              aspectRatio="1 / 1"
              width={120}
              height={120}
            />
          </div>
          <div className="header-content">
            <h1>{job.title}</h1>
            <h2 className="company-name">{job.hiringOrganization?.name}</h2>
            
            {/* Status Badges */}
            <JobStatusBadges 
              status={job.status}
              jobType={job.jobType}
              location={job.location}
              isActive={job.isActive}
            />
            
            <div className="job-meta">
              <span><FaMapMarkerAlt /> {job.location?.address || job.location}</span>
              <span><FaBriefcase /> {job.jobType}</span>
              <span><FaClock /> Posted {new Date(job.postedDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="job-detail-content">
        <div className="main-content">
          {/* Ad Before Job Description */}
          <AdUnitJobs />

          <section className="job-description">
            <h2>Job Description</h2>
            <div 
              className="description rich-text-content"
              dangerouslySetInnerHTML={{ __html: job.description || job.content }}
            />
          </section>
          
          {/* Job Overview */}
          <JobOverview job={job} />

          {/* Ad After Job Overview */}
          <AdUnitJobs />


          
          {/* Company Information */}
          <CompanyInfo 
            company={job.hiringOrganization}
            contactInfo={job.contactInfo}
          />

          {job.requirements && (
            <section className="job-requirements">
              <h2>Requirements</h2>
              <div 
                className="requirements-list"
                dangerouslySetInnerHTML={{ __html: job.requirements }}
              />
            </section>
          )}

          {job.responsibilities && (
            <section className="job-responsibilities">
              <h2>Responsibilities</h2>
              <div 
                className="responsibilities-list rich-text-content"
                dangerouslySetInnerHTML={{ __html: job.responsibilities }}
              />
            </section>
          )}

          {job.skills && job.skills.length > 0 && (
            <section className="job-skills">
              <h2>Required Skills</h2>
              <div className="skills-list">
                {job.skills.map((skill, index) => (
                  <span key={index} className="skill-tag">
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          {job.jobBenefits && (
            <section className="job-benefits">
              <h2>Benefits</h2>
              <div 
                className="benefits-list rich-text-content"
                dangerouslySetInnerHTML={{ __html: job.jobBenefits }}
              />
            </section>
          )}

          {job.applicationInstructions && (
            <section className="application-instructions">
              <h2>How to Apply</h2>
              <div 
                className="instructions-content rich-text-content"
                dangerouslySetInnerHTML={{ __html: job.applicationInstructions }}
              />
            </section>
          )}
        </div>

        <aside className="job-overview-sidebar">
          {/* Ad in Sidebar */}
          <AdUnitJobs />

          <div className="overview-card">
            <div className="job-alerts-section">
              <h3>Job Alerts</h3>
              <div className="job-alerts-form">
                {alertStatus.success ? (
                  <div className="alert-success">
                    <p>Successfully subscribed to job alerts!</p>
                    <p>You will receive:</p>
                    <ul>
                      <li>A welcome email with platform information</li>
                      <li>Email notifications for new jobs in your selected categories</li>
                      <li>Regular updates about matching opportunities</li>
                    </ul>
                    <p className="alert-note">
                      You can manage your subscription preferences in your profile settings.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleAlertSubmit}>
                    <div className="form-group">
                      <label>Email</label>
                      <input 
                        type="email" 
                        name="email"
                        value={alertForm.email}
                        onChange={handleAlertInputChange}
                        placeholder="Enter your email"
                        className="alert-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Job Categories</label>
                      <select 
                        multiple
                        className="alert-select"
                        value={alertForm.jobCategories}
                        onChange={handleCategoryChange}
                        required
                      >
                        {jobCategories.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                      <small className="select-help">Hold Ctrl (Windows) or Command (Mac) to select multiple categories</small>
                    </div>
                    {alertStatus.error && (
                      <div className="alert-error">
                        {alertStatus.error}
                      </div>
                    )}
                    <button 
                      type="submit" 
                      className="save-alert-btn"
                      disabled={alertStatus.loading || alertForm.jobCategories.length === 0}
                    >
                      {alertStatus.loading ? 'Subscribing...' : 'Subscribe to Job Alerts'}
                    </button>
                    <p className="privacy-note">
                      By subscribing, you agree to receive job alerts and updates. You can unsubscribe at any time.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Recent Jobs Section for Desktop */}
          <div className="overview-card recent-jobs-sidebar">
            <div className="recent-jobs-section">
              <h3>Recent Jobs</h3>
              <RecentJobs currentJob={job} limit={15} variant="sidebar" />
            </div>
          </div>
        </aside>
      </div>
      
      <section className="share-section">
        <ShareJobButtons 
          jobTitle={job.title}
          company={job.hiringOrganization?.name}
          jobUrl={window.location.href}
          job={job}
        />
      </section>

      {/* Related Jobs Section - Mobile Only */}
      <div className="recent-jobs-mobile">
        <RecentJobs currentJob={job} limit={3} />
      </div>
    </div>
  );
};

export default JobDetail;
 